import { useCallback, useMemo, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import clsx from 'clsx';
import QueuePage from '../../components/ui/QueuePage';
import { Toast } from '../../components/ui/Toast';
import DispositionModal from '../../components/returns/DispositionModal';
import { useReturnDisposition } from '../../hooks/useReturnDisposition';
import { useApp } from '../../hooks/useApp';

interface ReturnRow {
  id: string;
  return_number?: string | null;
  isg_sku?: string | null;
  title?: string | null;
  channel?: string | null;
  status?: string | null;
  condition_received?: string | null;
  quantity?: number | null;
  distributor_name?: string | null;
  created_at?: string | null;
}

const STATUS_CLS: Record<string, string> = {
  initiated: 'bg-surface-3 text-ink-secondary',
  in_transit: 'bg-info/15 text-info',
  received: 'bg-warning/15 text-warning',
  inspection: 'bg-accent/15 text-accent',
};
const STATUS_LABEL: Record<string, string> = {
  initiated: 'Initiated',
  in_transit: 'In Transit',
  received: 'Received',
  inspection: 'Inspection',
};

function relativeTime(d: string | null | undefined): string {
  if (!d) return '\u2014';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ReturnsInspection(): JSX.Element {
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer + dist_rep are both read-only on Returns queues
  // (largely already read-only — no per-row mutation buttons, no bulk action
  // bar). readOnly prop hides the QueuePage checkbox column.
  const { isViewer, isDistRep } = useApp();
  // Cluster 8 (Rule 10): admin/user can set a disposition by clicking a return row.
  const disp = useReturnDisposition();
  const canDisposition = !isViewer && !isDistRep;

  const SkuCell = useCallback(
    (p: ICellRendererParams<ReturnRow>): JSX.Element | null => {
      if (!p.data) return null;
      const sku = p.data.isg_sku;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (sku) navigate(`/catalog/products/${sku}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {sku}
        </button>
      );
    },
    [navigate]
  );

  const StatusCell = useCallback((p: ICellRendererParams<ReturnRow>): JSX.Element | null => {
    const status = p.data?.status;
    if (!status) return null;
    return (
      <span
        className={clsx(
          'px-1.5 py-0.5 rounded text-2xs font-medium',
          STATUS_CLS[status] || 'bg-surface-3 text-ink-muted'
        )}
      >
        {STATUS_LABEL[status] || status}
      </span>
    );
  }, []);

  const columns = useMemo<ColDef<ReturnRow>[]>(
    () =>
      [
        {
          headerName: 'Return #',
          field: 'return_number',
          width: 130,
          pinned: 'left',
          suppressMovable: true,
          resizable: false,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
          },
        },
        {
          headerName: 'ISG SKU',
          field: 'isg_sku',
          width: 120,
          cellRenderer: SkuCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 180,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Channel',
          field: 'channel',
          width: 100,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          cellRenderer: StatusCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Condition',
          field: 'condition_received',
          width: 100,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Qty',
          field: 'quantity',
          width: 70,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Distributor',
          field: 'distributor_name',
          width: 130,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Created',
          field: 'created_at',
          width: 110,
          valueFormatter: (p: ValueFormatterParams<ReturnRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
      ] as ColDef<ReturnRow>[],
    [SkuCell, StatusCell]
  );

  return (
    <>
      <QueuePage
        title="Inspection"
        rpcName="get_returns_queue"
        rpcParams={{ p_status: null }}
        columns={columns}
        statusTabs={[
          { key: 'all', label: 'All' },
          { key: 'initiated', label: 'Initiated' },
          { key: 'in_transit', label: 'In Transit' },
          { key: 'received', label: 'Received' },
          { key: 'inspection', label: 'Inspection' },
        ]}
        emptyIcon={RotateCcw}
        emptyMessage="No returns in inspection — returns appear here when logged"
        searchPlaceholder="Search return #, SKU, or title..."
        readOnly={isViewer || isDistRep}
        onRowClicked={canDisposition ? (d) => disp.open(d as ReturnRow) : undefined}
        refreshSignal={disp.refreshSignal}
      />
      <DispositionModal
        open={!!disp.target}
        item={disp.target}
        onConfirm={disp.confirm}
        onCancel={disp.cancel}
        submitting={disp.submitting}
        error={disp.error}
      />
      <Toast toast={disp.toast} onDismiss={() => disp.setToast(null)} />
    </>
  );
}
